/**
 * CacheExplorerTab
 *
 * Live "mini Redis UI" — auto-refreshing cache statistics (hit rate, hits, misses,
 * key count, memory) plus a SCAN-based key browser. Each key can be inspected to
 * view its stored value, type and TTL.
 */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
  IconRefresh,
  IconEye,
  IconSearch,
  IconKey,
  IconPercentage,
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconPlayerPause,
  IconAlertCircle,
} from '@tabler/icons-react';
import {
  Stack,
  Group,
  Text,
  Grid,
  Card,
  Badge,
  Button,
  Alert,
  Loader,
  ActionIcon,
  TextInput,
} from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { useCacheExplorer } from '../hooks/useCacheExplorer';
import { CacheValueInspectorModal } from './CacheValueInspectorModal';
import type { CacheKeyValue } from '../types';

const REFRESH_INTERVAL_MS = 5000;

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
};

type KeyRow = { id: string; key: string; service: string };

interface StatCardProps {
  label: string;
  value: string;
  variant: 'primary' | 'success' | 'info' | 'secondary' | 'danger';
  icon: ReactNode;
}

function StatCard({ label, value, variant, icon }: StatCardProps) {
  return (
    <Grid.Col span={{ base: 6, md: 3 }}>
      <Card variant="gradient" gradientVariant={variant} padding="md">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="white" style={{ opacity: 0.9 }}>
              {label}
            </Text>
            {icon}
          </Group>
          <Text size="xl" fw={700} c="white">
            {value}
          </Text>
        </Stack>
      </Card>
    </Grid.Col>
  );
}

export function CacheExplorerTab() {
  const { stats, statsError, loading, fetchStats, scanKeys, getKeyValue } = useCacheExplorer();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Key browser
  const [pattern, setPattern] = useState('*');
  const [keys, setKeys] = useState<string[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);

  // Value inspector
  const [inspectKey, setInspectKey] = useState<string | null>(null);
  const [keyValue, setKeyValue] = useState<CacheKeyValue | null>(null);
  const [valueLoading, setValueLoading] = useState(false);
  const [valueError, setValueError] = useState<string | null>(null);

  const runScan = useCallback(
    async (scanPattern: string) => {
      setKeysLoading(true);
      setKeysError(null);
      try {
        const result = await scanKeys(scanPattern);
        setKeys(result.keys);
      } catch (err: any) {
        setKeysError(err?.message || 'Failed to scan keys');
        setKeys([]);
      } finally {
        setKeysLoading(false);
      }
    },
    [scanKeys]
  );

  // Initial load
  useEffect(() => {
    fetchStats();
    runScan('*');
  }, [fetchStats, runScan]);

  // Auto-refresh statistics only — the key list is scanned on demand.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(fetchStats, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchStats]);

  const handleInspect = useCallback(
    async (key: string) => {
      setInspectKey(key);
      setKeyValue(null);
      setValueError(null);
      setValueLoading(true);
      try {
        setKeyValue(await getKeyValue(key));
      } catch (err: any) {
        setValueError(err?.message || 'Could not load this key');
      } finally {
        setValueLoading(false);
      }
    },
    [getKeyValue]
  );

  const rows: KeyRow[] = useMemo(
    () => keys.map((k) => ({ id: k, key: k, service: k.split(':')[1] ?? '—' })),
    [keys]
  );

  const columns: DataTableColumn<KeyRow>[] = useMemo(
    () => [
      {
        key: 'key',
        label: 'Key',
        sortable: true,
        render: (value) => (
          <Text size="sm" fw={500} style={{ wordBreak: 'break-all' }}>
            {value}
          </Text>
        ),
      },
      {
        key: 'service',
        label: 'Service',
        sortable: true,
        render: (value) => (
          <Badge color="grape" variant="light">
            {value}
          </Badge>
        ),
      },
      {
        key: 'actions',
        label: '',
        render: (_, row) => (
          <ActionIcon
            variant="subtle"
            onClick={() => handleInspect(row.key)}
            aria-label={`Inspect ${row.key}`}
          >
            <IconEye size={16} />
          </ActionIcon>
        ),
      },
    ],
    [handleInspect]
  );

  // Hit rate computed from raw hits/misses so it is correct regardless of how the
  // backend reports its own hitRate field.
  const totalLookups = (stats?.hits ?? 0) + (stats?.misses ?? 0);
  const hitRatePct = totalLookups > 0 ? ((stats?.hits ?? 0) / totalLookups) * 100 : 0;

  if (loading && !stats) {
    return (
      <Group justify="center" p="xl">
        <Loader size="lg" />
      </Group>
    );
  }

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {stats?.timestamp
            ? `Updated ${new Date(stats.timestamp).toLocaleTimeString('en-ZA')}`
            : 'Not yet loaded'}
        </Text>
        <Group gap="xs">
          <Button
            variant={autoRefresh ? 'light' : 'subtle'}
            leftSection={autoRefresh ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />}
            onClick={() => setAutoRefresh((v) => !v)}
          >
            {autoRefresh ? 'Live (5s)' : 'Paused'}
          </Button>
          <Button variant="subtle" leftSection={<IconRefresh size={16} />} onClick={fetchStats} loading={loading}>
            Refresh
          </Button>
        </Group>
      </Group>

      {statsError && (
        <Alert color="orange" icon={<IconAlertCircle size={16} />} title="Redis statistics unavailable">
          {statsError}
        </Alert>
      )}

      {stats && (
        <>
          {/* Cache stats */}
          <Grid>
            <StatCard
              label="Hit Rate"
              value={`${hitRatePct.toFixed(1)}%`}
              variant="info"
              icon={<IconPercentage size={20} color="white" style={{ opacity: 0.85 }} />}
            />
            <StatCard
              label="Hits"
              value={stats.hits.toLocaleString()}
              variant="success"
              icon={<IconCheck size={20} color="white" style={{ opacity: 0.85 }} />}
            />
            <StatCard
              label="Misses"
              value={stats.misses.toLocaleString()}
              variant="danger"
              icon={<IconX size={20} color="white" style={{ opacity: 0.85 }} />}
            />
            <StatCard
              label="Total Keys"
              value={stats.itemCount.toLocaleString()}
              variant="primary"
              icon={<IconKey size={20} color="white" style={{ opacity: 0.85 }} />}
            />
          </Grid>

          <Group gap="xl">
            <Text size="sm">
              Memory used:{' '}
              <Text span fw={700}>
                {formatBytes(stats.memoryUsageBytes)}
              </Text>
            </Text>
            <Text size="sm">
              Total operations:{' '}
              <Text span fw={700}>
                {stats.totalOperations.toLocaleString()}
              </Text>
            </Text>
          </Group>
        </>
      )}

      {/* Key browser */}
      <Group gap="xs">
        <TextInput
          placeholder="Key pattern, e.g. ccms:identity:*  (use * for all)"
          leftSection={<IconSearch size={16} />}
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runScan(pattern);
          }}
          style={{ flex: 1 }}
        />
        <Button leftSection={<IconSearch size={16} />} onClick={() => runScan(pattern)} loading={keysLoading}>
          Scan
        </Button>
      </Group>

      {keysError ? (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not scan keys">
          {keysError}
        </Alert>
      ) : (
        <>
          <Text size="xs" c="dimmed">
            {keys.length === 0
              ? 'No keys matched.'
              : `Showing ${keys.length} key(s) — refine the pattern to narrow results.`}
          </Text>
          <Card>
            <DataTable columns={columns} data={rows} striped highlightOnHover />
          </Card>
        </>
      )}

      <CacheValueInspectorModal
        opened={inspectKey !== null}
        onClose={() => setInspectKey(null)}
        cacheKey={inspectKey ?? ''}
        data={keyValue}
        loading={valueLoading}
        error={valueError}
      />
    </Stack>
  );
}
